import { computed, makeObservable } from 'mobx';
import { matchPath } from 'react-router';

import { IRootStore } from 'shared/entities/store/rootStore';
import { Permission } from 'shared/entities/permissions';
import { IProjectModel } from 'shared/entities/projects';
import { ICabinetModel } from 'shared/entities/cabinet';
import { CabinetManagerRoleType } from 'shared/entities/manager';
import { urls } from 'shared/entities/domain';
import { ROLE_ALLOWED_MASKS } from 'shared/entities/permissions/domain';

export default class PermissionsStore {
  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      cabinet: computed,
      canEditManagers: computed,
      canViewBilling: computed,
      canViewManagers: computed,
      canEditProject: computed,
      canViewProject: computed,
      canCreateProject: computed,
      canPublishScenarios: computed,
      canViewProjectManagers: computed,
      canCreateCabinet: computed,
      canRemoveProject: computed,
      canRemoveCabinet: computed,
      canEditCabinet: computed,
      canViewConversations: computed,
      canEditConversations: computed,
      canManageUserData: computed,
      canViewOperatorAnalytics: computed,
      isOperator: computed,
      shouldPay: computed,
      canViewUnpublishedFeatures: computed,
      canManageTemplate: computed,
      isELamaUser: computed,
      canViewBalancePage: computed
    });
  }

  get cabinet(): ICabinetModel | null {
    return this.rootStore.cabinetStore.entity;
  }

  get project(): IProjectModel | null {
    return this.rootStore.projectStore.entity;
  }

  get canEditManagers(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.EDIT_MANAGERS)
      : false;
  }

  get canViewBilling(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.ACCESS_BILLING)
      : false;
  }

  get canViewBalancePage(): boolean {
    if (!this.cabinet) {
      return false;
    }
    return this.canViewBilling && !this.isELamaUser;
  }

  get canViewManagers(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.VIEW_MANAGERS)
      : false;
  }

  get canViewProjectManagers(): boolean {
    return this.project
      ? this.project.permissions.includes(Permission.VIEW_MANAGERS)
      : false;
  }

  get canViewOperatorAnalytics(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.VIEW_OPERATOR_ANALYTICS)
      : false;
  }

  get canEditProject(): boolean {
    return this.project
      ? this.project.permissions.includes(Permission.EDIT_PROJECT)
      : false;
  }

  get canViewProject(): boolean {
    return this.project
      ? this.project.permissions.includes(Permission.VIEW_PROJECT)
      : false;
  }

  get canPublishScenarios(): boolean {
    if (
      this.rootStore.pluginsStore.notConsideredRoles.includes(
        Permission.PUBLISH_SCENARIOS
      )
    ) {
      return this.canEditProject;
    }

    return this.project
      ? this.project.permissions.includes(Permission.PUBLISH_SCENARIOS)
      : false;
  }

  get canCreateProject(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.CREATE_PROJECT)
      : false;
  }

  get canCreateCabinet(): boolean {
    if (
      this.rootStore.pluginsStore.notConsideredRoles.includes(
        Permission.CREATE_CABINET
      )
    ) {
      return true;
    }

    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.CREATE_CABINET)
      : false;
  }

  get canRemoveProject(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.DELETE_PROJECT)
      : false;
  }

  get canRemoveCabinet(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.DELETE_CABINET)
      : false;
  }

  get canEditCabinet(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.EDIT_CABINET)
      : false;
  }

  get canViewUnpublishedFeatures(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(
          Permission.SA_VIEW_UNPUBLISHED_FEATURES
        )
      : false;
  }

  get canManageTemplate(): boolean {
    return this.cabinet
      ? this.cabinet.permissions.includes(Permission.SA_MANAGE_TEMPLATES)
      : false;
  }

  get canViewConversations(): boolean {
    return this.project
      ? this.project.permissions.includes(Permission.VIEW_CONVERSATIONS)
      : false;
  }

  get canEditConversations(): boolean {
    return this.project
      ? this.project.permissions.includes(Permission.EDIT_CONVERSATIONS)
      : false;
  }

  get canManageUserData(): boolean {
    return this.canEditProject || this.canEditConversations;
  }

  get isOperator(): boolean {
    return this.cabinet?.role === CabinetManagerRoleType.operator;
  }

  get shouldPay(): boolean {
    return !!this.rootStore.cabinetStore.entity?.shouldPay;
  }

  get isELamaUser(): boolean {
    return !!this.rootStore.userStore.userExtra?.registeredViaElama;
  }

  getDefaultProjectUrl(projectId: string): string {
    return this.isOperator
      ? urls.PROJECT.tabs.CONVERSATIONS.create({ projectId })
      : urls.PROJECT.tabs.SCENARIOS.COMMON.create({ projectId });
  }

  isAllowedProjectRoute(path: string): boolean {
    const role = this.cabinet?.role;
    if (!role) {
      return false;
    }

    const allowedMasks = ROLE_ALLOWED_MASKS[role];
    if (allowedMasks && !matchPath(path, { path: allowedMasks })) {
      return false;
    }

    if (
      matchPath(path, {
        path: urls.PROJECT.tabs.OPERATOR_ANALYTICS.mask
      })
    ) {
      return this.canViewOperatorAnalytics;
    }

    return true;
  }
}
